import {
  CARK_BEKLEME_SAAT_EN_AZ,
  CARK_BEKLEME_SAAT_EN_COK,
  CARK_DILIM_EN_AZ,
  CARK_DILIM_EN_COK,
  CARK_PUAN_EN_COK,
  CARK_TOPLAM_AGIRLIK_EN_COK,
  GEREKCE_AGIR_EN_AZ,
  GEREKCE_EN_COK,
  carkTabloIstegiSemasi,
  type CarkTabloIstegi,
} from "@koclukpanel/contracts";

/**
 * ÇARK TABLO DÜZENLEME — saf hesap ve doğrulama (F-GAM-19, sürüm 7b kararlar).
 *
 * ÖNİZLEME YALNIZ GÖSTERİMDİR: olasılık, nadirlik ve beklenen ortalama burada ekran için hesaplanır; asıl doğrulamayı ve
 * kaydı SUNUCU yapar (`carkTabloIstegiSemasi` + veritabanı tetikleyicisi). Bu dosya sunucunun kurallarını yalnız
 * "Kaydet" düğmesini boşuna açmamak için yansıtır — sınırlar `packages/contracts/src/oyun-b.ts`'ten okunur, kopyalanmaz.
 *
 * Form alanları METİN tutulur (kullanıcı yarım yazı yazabilir); hesap yalnız tam sayı olarak okunabilen alanlarla yapılır.
 */

export interface DilimGirdisi {
  /** Yalnız React anahtarı: satır eklenip silinirken alanlar karışmasın. */
  anahtar: number;
  puan: string;
  agirlik: string;
}

/** Düz rakam dizisi → tam sayı; boş, ondalık, işaretli ya da güvenli tam sayı aşan girdi → null. */
export function tamSayiOku(metin: string): number | null {
  const t = metin.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return Number.isSafeInteger(n) ? n : null;
}

const SAYI = (n: number, enCok = 0): string =>
  n.toLocaleString("tr-TR", { maximumFractionDigits: enCok });

/** Yüzde metni: "%60", "%2,9", "%0,0074". Sıfırdan büyük ama gösterilemeyecek kadar küçükse "%<0,0001". */
export function yuzdeMetni(agirlik: number, toplam: number): string {
  if (toplam <= 0 || agirlik <= 0) return "%0";
  const yuzde = (agirlik / toplam) * 100;
  if (yuzde < 0.0001) return "%<0,0001";
  return `%${SAYI(yuzde, yuzde >= 1 ? 2 : 4)}`;
}

/**
 * Nadirlik: tam bölünüyorsa "1/1.250", bölünmüyorsa "yaklaşık 1/1.250" (N tam sayıya yuvarlanır, tr-TR binlik ayraç).
 * Ağırlık sıfırsa dilim hiç çıkmaz; ağırlık toplama eşitse (tek seçilebilir dilim) her çevirmede çıkar.
 */
export function nadirlikMetni(agirlik: number, toplam: number): string {
  if (agirlik <= 0) return "çıkmaz";
  if (toplam <= 0) return "";
  if (agirlik === toplam) return "her çevirmede";
  const n = Math.round(toplam / agirlik);
  return `${toplam % agirlik === 0 ? "" : "yaklaşık "}1/${SAYI(n)}`;
}

export interface SatirOnizleme {
  yuzde: string;
  nadirlik: string;
}

export interface Onizleme {
  /** Ağırlığı geçerli okunabilen satırların toplamı. */
  toplam: number;
  /** Satır başına önizleme; ağırlığı okunamayan satır ya da toplam sıfırken `null`. */
  satirlar: (SatirOnizleme | null)[];
  /** Σ puan×ağırlık / toplam. Her satırın puanı ve ağırlığı geçerli ve toplam > 0 değilse `null`. */
  beklenen: number | null;
}

export function onizlemeHesapla(satirlar: readonly DilimGirdisi[]): Onizleme {
  const agirliklar = satirlar.map((s) => tamSayiOku(s.agirlik));
  const toplam = agirliklar.reduce<number>((t, a) => t + (a ?? 0), 0);
  const satirOnizleme = agirliklar.map((a) =>
    a === null || toplam <= 0
      ? null
      : { yuzde: yuzdeMetni(a, toplam), nadirlik: nadirlikMetni(a, toplam) },
  );
  let beklenen: number | null = null;
  if (toplam > 0) {
    let agirlikliToplam = 0;
    let tamam = true;
    satirlar.forEach((s, i) => {
      const p = tamSayiOku(s.puan);
      const a = agirliklar[i] ?? null;
      if (p === null || a === null) tamam = false;
      else agirlikliToplam += p * a;
    });
    if (tamam) beklenen = agirlikliToplam / toplam;
  }
  return { toplam, satirlar: satirOnizleme, beklenen };
}

/** "9,95" — iki ondalık, tr-TR. */
export function ortalamaMetni(n: number): string {
  return n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Haftalık çevirme üst sınırı (hak aralığı saat): 24 saat → 7. Geçersiz aralıkta null. */
export function haftalikCevirme(beklemeSaat: number | null): number | null {
  if (beklemeSaat === null || beklemeSaat < CARK_BEKLEME_SAAT_EN_AZ) return null;
  return Math.max(1, Math.floor(168 / beklemeSaat));
}

export interface SatirHatasi {
  puan?: string;
  agirlik?: string;
}

export interface Dogrulama {
  satirlar: SatirHatasi[];
  bekleme?: string;
  gerekce?: string;
  /** Alan altında gösterilmeyen, Kaydet'in neden kapalı olduğunu söyleyen cümleler (boş alan, dilim sınırı, toplam...). */
  genel: string[];
  /** Sunucuya gidecek gövde; hata varsa null. */
  govde: CarkTabloIstegi | null;
}

export interface FormGirdisi {
  satirlar: readonly DilimGirdisi[];
  bekleme: string;
  gerekce: string;
  oncekiSurum: number;
  /** Açıldığı andaki etkin bekleme: değişmediyse gövdeye yazılmaz (sunucu etkin sürümünkini korur). */
  mevcutBekleme: number;
}

/** Alan hatası yalnız DOLU ama geçersiz girdide gösterilir; boş alan "Kaydet kapalı" açıklamasına girer. */
export function dogrula(f: FormGirdisi): Dogrulama {
  const genel: string[] = [];
  const satirlar: SatirHatasi[] = f.satirlar.map(() => ({}));
  let bosAlan = false;

  if (f.satirlar.length < CARK_DILIM_EN_AZ) {
    genel.push(`Çarkta en az ${CARK_DILIM_EN_AZ} dilim olmalı.`);
  }
  if (f.satirlar.length > CARK_DILIM_EN_COK) {
    genel.push(`Çarkta en çok ${CARK_DILIM_EN_COK} dilim olabilir.`);
  }

  let oncekiPuan: number | null = null;
  let toplam = 0;
  f.satirlar.forEach((s, i) => {
    const hata = satirlar[i]!;
    const puanMetni = s.puan.trim();
    const agirlikMetni = s.agirlik.trim();
    const puan = tamSayiOku(puanMetni);
    const agirlik = tamSayiOku(agirlikMetni);

    if (puanMetni === "") bosAlan = true;
    else if (puan === null) hata.puan = "Puan tam sayı olmalı.";
    else if (puan < 1 || puan > CARK_PUAN_EN_COK) {
      hata.puan = `Puan 1 ile ${SAYI(CARK_PUAN_EN_COK)} arasında olmalı.`;
    } else if (oncekiPuan !== null && puan <= oncekiPuan) {
      hata.puan = "Puan bir önceki dilimden büyük olmalı (puanlar tekil ve artan).";
    }
    if (puan !== null && puan >= 1) oncekiPuan = puan;

    if (agirlikMetni === "") bosAlan = true;
    else if (agirlik === null) hata.agirlik = "Ağırlık 0 ya da daha büyük bir tam sayı olmalı.";
    else if (agirlik > CARK_TOPLAM_AGIRLIK_EN_COK) {
      hata.agirlik = `Ağırlık en çok ${SAYI(CARK_TOPLAM_AGIRLIK_EN_COK)} olabilir.`;
    } else toplam += agirlik;
  });

  if (bosAlan) genel.push("Boş puan ya da ağırlık alanlarını doldurun.");

  const tumAgirliklarGecerli = f.satirlar.every((s) => tamSayiOku(s.agirlik) !== null);
  if (tumAgirliklarGecerli) {
    if (toplam <= 0) {
      genel.push("Tüm ağırlıklar sıfır: en az bir dilimin ağırlığı sıfırdan büyük olmalı.");
    } else if (toplam > CARK_TOPLAM_AGIRLIK_EN_COK) {
      genel.push(`Toplam ağırlık en çok ${SAYI(CARK_TOPLAM_AGIRLIK_EN_COK)} olabilir.`);
    }
  }

  const bekleme = tamSayiOku(f.bekleme);
  let beklemeHatasi: string | undefined;
  if (f.bekleme.trim() === "") genel.push("Hak aralığını (saat) girin.");
  else if (
    bekleme === null ||
    bekleme < CARK_BEKLEME_SAAT_EN_AZ ||
    bekleme > CARK_BEKLEME_SAAT_EN_COK
  ) {
    beklemeHatasi = `Hak aralığı ${CARK_BEKLEME_SAAT_EN_AZ} ile ${CARK_BEKLEME_SAAT_EN_COK} saat arasında tam sayı olmalı.`;
  }

  const gerekce = f.gerekce.trim();
  let gerekceHatasi: string | undefined;
  if (gerekce === "") genel.push(`Gerekçe zorunlu (en az ${GEREKCE_AGIR_EN_AZ} karakter).`);
  else if (gerekce.length < GEREKCE_AGIR_EN_AZ) {
    gerekceHatasi = `Gerekçe en az ${GEREKCE_AGIR_EN_AZ} karakter olmalı.`;
  } else if (gerekce.length > GEREKCE_EN_COK) {
    gerekceHatasi = `Gerekçe en çok ${GEREKCE_EN_COK} karakter olabilir.`;
  }

  const alanHatasi =
    satirlar.some((h) => h.puan || h.agirlik) ||
    beklemeHatasi !== undefined ||
    gerekceHatasi !== undefined;
  const sonuc: Dogrulama = {
    satirlar,
    genel,
    govde: null,
    ...(beklemeHatasi ? { bekleme: beklemeHatasi } : {}),
    ...(gerekceHatasi ? { gerekce: gerekceHatasi } : {}),
  };
  if (alanHatasi || genel.length > 0 || bekleme === null) return sonuc;

  const aday = {
    oncekiSurum: f.oncekiSurum,
    dilimler: f.satirlar.map((s) => ({
      puan: tamSayiOku(s.puan) ?? 0,
      agirlik: tamSayiOku(s.agirlik) ?? 0,
    })),
    ...(bekleme !== f.mevcutBekleme ? { bekleme } : {}),
    gerekce,
  };
  // Sunucu sözleşmesi son söz: ekranın kuralları ondan sapsa bile Kaydet açılmaz.
  const ayrisma = carkTabloIstegiSemasi.safeParse(aday);
  if (!ayrisma.success) {
    sonuc.genel.push(ayrisma.error.issues[0]?.message ?? "Tablo sözleşmeye uymuyor.");
    return sonuc;
  }
  sonuc.govde = ayrisma.data;
  return sonuc;
}

/** Beklenen ortalama bu orandan fazla değişiyorsa onay özetinde vurgulu uyarı (engelleme yok). */
export const YUKSEK_DEGISIKLIK_ORANI = 0.5;

export function yuksekDegisiklikMi(mevcut: number, yeni: number): boolean {
  if (!(mevcut > 0)) return yeni > 0;
  return Math.abs(yeni - mevcut) / mevcut > YUKSEK_DEGISIKLIK_ORANI;
}
